import type { Footer } from "@/types/blocks/footer";
import type {
  MiroFishGraphPayload,
  MiroFishReportPayload,
  MiroFishScenario,
  MiroFishSimulationPayload,
} from "@/types/mirofish";
import type {
  PredictionDomainIcon,
  PredictionPageConfig,
} from "@/content/prediction-page";
import { WORLD_CUP_PREDICTION_PATH } from "@/content/world-cup-prediction";
import { SUPPORT_MAILTO, VISIBLE_LOGO_SRC } from "@/lib/brand";

type MiroFishPredictionNavLink = {
  label: string;
  description: string;
  category: string;
  icon: PredictionDomainIcon;
  href: PredictionPageConfig["path"];
};

type MiroFishNavLink =
  | { kind: "hash"; label: string; href: "#workflow" | "#faq" }
  | {
      kind: "route";
      label: string;
      href: "/pricing" | "/blog" | "/use-cases";
    }
  | {
      kind: "prediction-menu";
      label: "Use Cases";
      collectionHref: "/use-cases";
      items: MiroFishPredictionNavLink[];
    };

export const mirofishPredictionNavLinks = [
  {
    label: "Market Simulation AI",
    description: "Stress-test launches, pricing, and market entry.",
    category: "Market",
    icon: "market",
    href: "/use-cases/market-simulation-ai",
  },
  {
    label: "World Cup Prediction",
    description: "Build an evidence-led 2026 match forecast.",
    category: "Sports",
    icon: "sports",
    href: WORLD_CUP_PREDICTION_PATH,
  },
] satisfies MiroFishPredictionNavLink[];

export const mirofishNavLinks = [
  { kind: "hash", label: "Workflow", href: "#workflow" },
  {
    kind: "prediction-menu",
    label: "Use Cases",
    collectionHref: "/use-cases",
    items: mirofishPredictionNavLinks,
  },
  { kind: "route", label: "Pricing", href: "/pricing" },
  { kind: "route", label: "Blog", href: "/blog" },
  { kind: "hash", label: "FAQ", href: "#faq" },
] satisfies MiroFishNavLink[];

export const mirofishHomeStats = [
  { label: "Parallel agents per run", value: "1M+" },
  { label: "Simulation surfaces", value: "Twitter + Reddit" },
  { label: "Primary output", value: "Prediction report" },
];

export const mirofishHomeScenarioCards = [
  {
    title: "Public Opinion",
    summary:
      "Model how institutions, media, influencers, and observers reshape the first narrative around an incident.",
    input: "Incident brief + policy memo",
    output: "Narrative escalation map",
    prompt:
      "Forecast how public reaction evolves after this incident, who amplifies it first, and what response slows trust erosion.",
    articleSlug: "public-opinion-simulation-with-ai-agents",
  },
  {
    title: "Launch Reaction",
    summary:
      "Stress-test product messaging before competitors, users, and commentators interpret the launch for you.",
    input: "Launch brief + FAQ",
    output: "Top misunderstanding risk",
    prompt:
      "Simulate how customers, competitors, and online commentators react to this launch, and identify the most expensive misunderstanding.",
    articleSlug: "product-launch-reaction-simulation",
  },
  {
    title: "Policy Impact",
    summary:
      "Inspect how different stakeholder groups interpret a draft policy once incentives and compliance pressure collide.",
    input: "Policy draft + stakeholder notes",
    output: "Downstream pressure analysis",
    prompt:
      "Simulate how institutions, affected groups, and public commentators interpret this policy draft, and identify the largest downstream risk.",
    articleSlug: "policy-impact-simulation-with-agent-swarms",
  },
  {
    title: "Brand Crisis",
    summary:
      "See how a fragile launch or reputational event expands when the public question drifts away from internal intent.",
    input: "Risk memo + response plan",
    output: "Crisis response gap",
    prompt:
      "Simulate a hostile launch reaction to the uploaded brief and explain where the crisis response plan fails under time pressure.",
    articleSlug: "crisis-pr-simulation-before-launch",
  },
  {
    title: "Finance Case",
    summary:
      "Run a market-facing scenario where management, analysts, and retail narratives react to the same financial signal differently.",
    input: "Earnings note + market context",
    output: "Sentiment divergence summary",
    prompt:
      "Forecast how the uploaded financial brief changes analyst, investor, and public reaction across several rounds of interpretation.",
    articleSlug: "mirofish-vs-traditional-forecasting",
  },
  {
    title: "Literary Continuation",
    summary:
      "Treat a fictional world as a live graph of motives and memory, then test how one new event changes the story.",
    input: "Narrative chapter + continuation brief",
    output: "Character pressure graph",
    prompt:
      "Simulate how this fictional scenario evolves, which characters gain influence first, and what tension changes the ending.",
    articleSlug: "how-mirofish-simulates-the-future",
  },
];

export const mirofishForecastExplorerTabs = [
  {
    key: "graph",
    label: "Graph",
    eyebrow: "Actor Surface",
    title: "Inspect the pressure structure before you trust the answer.",
    description:
      "A useful forecast starts with visible actors, tensions, and constraint layers instead of one hidden blob of reasoning.",
    prompt:
      "Map the actors, tensions, and missing constraints in this source material before the simulation begins.",
    bullets: [
      "Institutional actor linked to student sentiment stream",
      "Media amplification node already connected to rumor pressure",
      "Constraint layer shows weak official response timing",
    ],
  },
  {
    key: "simulation",
    label: "Simulation",
    eyebrow: "Round Trace",
    title: "Watch reaction move across platforms and memory layers.",
    description:
      "The simulation surface reveals how one interpretation becomes dominant, challenged, or fragmented over multiple rounds.",
    prompt:
      "Simulate three rounds of reaction to this event and show which actor gains narrative control first.",
    bullets: [
      "Round 1: influencers turn the event into a trust question",
      "Round 2: mainstream media reframes institutional intent",
      "Round 3: public comments split between backlash and fatigue",
    ],
  },
  {
    key: "report",
    label: "Report",
    eyebrow: "Operator Readout",
    title: "Read the likely trajectory as an executive summary.",
    description:
      "The report compresses the path into a concise readout of risks, key actors, and the evidence line behind the forecast.",
    prompt:
      "Generate a forecast report from this scenario, including the dominant risk, key actors, and what evidence would change the outcome.",
    bullets: [
      "Dominant risk: sustained trust erosion from delayed response",
      "Key actor: narrative drivers outrun formal clarification",
      "Confidence weakens if new evidence changes the motive structure",
    ],
  },
] as const;

export const mirofishPromptRecipes = [
  {
    title: "Public Opinion Forecast",
    summary: "Best for incidents, controversies, and reputation-sensitive policy events.",
    prompt:
      "Forecast how the uploaded incident evolves across public platforms, who amplifies it first, and what response slows trust erosion over three rounds.",
  },
  {
    title: "Launch Stress Test",
    summary: "Best for product launches, open betas, and brand announcements.",
    prompt:
      "Simulate how customers, competitors, and commentators react to this launch brief, and identify the most expensive misunderstanding if left unanswered.",
  },
  {
    title: "Policy Reaction",
    summary: "Best for regulation, governance changes, and public institution messaging.",
    prompt:
      "Simulate how institutions, affected groups, and public commentators interpret this policy draft, and identify the largest downstream pressure.",
  },
  {
    title: "Narrative Continuation",
    summary: "Best for fiction, scenario writing, and character-driven world simulation.",
    prompt:
      "Simulate how this narrative world evolves after the new event, which characters gain influence first, and what tension changes the final outcome.",
  },
];

export const mirofishTrustPillars = [
  {
    title: "What MiroFish Simulates",
    points: [
      "Actor incentives and motive conflict",
      "Narrative spread across platform-style surfaces",
      "How one reaction changes the next round",
    ],
  },
  {
    title: "What Stays Human",
    points: [
      "Choosing the scenario boundary",
      "Judging whether the graph is missing pressure",
      "Making high-stakes operating calls",
    ],
  },
  {
    title: "Research Context",
    points: [
      "Generative agents and social simulation research",
      "Scenario planning framed as reviewable evidence",
      "Forecasts treated as inspectable hypotheses, not certainty",
    ],
  },
];

export const mirofishHomeGuideSlugs = [
  "what-is-mirofish",
  "how-mirofish-simulates-the-future",
  "what-is-multi-agent-simulation",
  "how-to-review-a-mirofish-forecast",
];

export const mirofishWorkflow = [
  {
    step: "01",
    title: "Ontology Generation",
    description:
      "Turn raw reports, notes, or fiction into structured entities, motives, and factual anchors.",
  },
  {
    step: "02",
    title: "Graph Construction",
    description:
      "Assemble a living relationship graph that exposes the actors, tensions, and memory structure behind the scenario.",
  },
  {
    step: "03",
    title: "Parallel Simulation",
    description:
      "Let platform-native agents interact across Twitter and Reddit style channels over multiple rounds.",
  },
  {
    step: "04",
    title: "Report Generation",
    description:
      "Condense the trajectory into a readable prediction report with key turning points, risks, and confidence signals.",
  },
  {
    step: "05",
    title: "Deep Interaction",
    description:
      "Interrogate the generated world through ReportAgent or by interviewing individual characters directly.",
  },
];

export const mirofishFaq = [
  {
    question: "What can I upload into MiroFish?",
    answer:
      "The current product shell accepts PDF, Markdown, and plain text. The workflow assumes the file contains enough narrative, analytical, or factual seed material to build a graph and simulation context.",
  },
  {
    question: "Does this really simulate multiple platforms?",
    answer:
      "The product flow is designed around a dual-surface simulation. In fixture mode the posts and actions are deterministic; in backend mode the Python service is responsible for generating platform-native behavior.",
  },
  {
    question: "Can I use it for public opinion and fiction?",
    answer:
      "Yes. The built-in scenarios cover public opinion events, literary continuation, market or finance cases, and a general-purpose predictive mode.",
  },
  {
    question: "What does the report include?",
    answer:
      "Each report includes an executive summary, predicted developments, major risks, evidence lines, key actors, and a long-form narrative explanation of how the outcome unfolds.",
  },
];

export const mirofishVideos = [
  {
    title: "Public Opinion Forecast Demo",
    href: "https://www.bilibili.com/video/BV1VYBsBHEMY/",
    image: "/images/mirofish/video-cover-wuhan.png",
    description:
      "A full walkthrough of how a seeded public opinion case expands into a simulation and report.",
  },
  {
    title: "Dream of the Red Chamber Demo",
    href: "https://www.bilibili.com/video/BV1cPk3BBExq/",
    image: "/images/mirofish/video-cover-red-chamber.jpg",
    description:
      "A literary continuation experiment showing how narrative seeds can drive a deep predictive world.",
  },
];

export const mirofishScreenshots = [
  "/images/mirofish/screenshots/01.png",
  "/images/mirofish/screenshots/02.png",
  "/images/mirofish/screenshots/03.png",
  "/images/mirofish/screenshots/04.png",
  "/images/mirofish/screenshots/05.png",
  "/images/mirofish/screenshots/06.png",
];

export const mirofishBrandCopy = {
  eyebrow: "A simple and universal swarm intelligence engine",
  title: "Upload Any Report. Simulate The Future Instantly.",
  description:
    "MiroFish transforms real-world seeds such as news, policy drafts, analytical reports, or fiction into a parallel world populated by agents with memory, motives, and social behavior.",
  subdescription:
    "From public opinion forecasting to literary continuation, it lets you inject variables, run social evolution, and inspect the likely trajectory before the real world catches up.",
  primaryCta: "Start Engine",
  secondaryCta: "Watch Demo",
};

export const mirofishFooter: Footer = {
  name: "footer",
  brand: {
    title: "MiroFish",
    description:
      "A product shell rebuilt in Next.js for graph-native predictive simulation, narrative stress testing, report generation, and deep interaction across a synthetic world.",
    logo: {
      src: VISIBLE_LOGO_SRC,
      alt: "MiroFish",
    },
    url: "/",
    target: "_self",
  },
  nav: {
    items: [
      {
        title: "Explore",
        children: [
          { title: "Workflow", url: "/#workflow", target: "_self" },
          { title: "Use Cases", url: "/use-cases", target: "_self" },
          {
            title: "Market Simulation AI",
            url: "/use-cases/market-simulation-ai",
            target: "_self",
          },
          {
            title: "World Cup Prediction",
            url: WORLD_CUP_PREDICTION_PATH,
            target: "_self",
          },
          {
            title: "Public Opinion Simulation",
            url: "/use-cases/public-opinion-simulation-ai",
            target: "_self",
          },
          { title: "History", url: "/#history", target: "_self" },
          { title: "Screenshots", url: "/#screenshots", target: "_self" },
          { title: "FAQ", url: "/#faq", target: "_self" },
        ],
      },
    ],
  },
  social: {
    items: [
      {
        title: "Email support",
        icon: "RiMailLine",
        url: SUPPORT_MAILTO,
        target: "_self",
      },
    ],
  },
  copyright: "All rights reserved.",
  disclaimer:
    "MiroFish is an independent simulation product. Forecasts are exploratory outputs and should be reviewed before operational, financial, or policy decisions.",
  agreement: {
    items: [
      { title: "Privacy Policy", url: "/privacy-policy", target: "_self" },
      { title: "Terms of Service", url: "/terms-of-service", target: "_self" },
      { title: "Refund Policy", url: "/refund-policy", target: "_self" },
    ],
  },
};

export const classifyScenario = (
  prompt: string,
  filenames: string[]
): MiroFishScenario => {
  const source = `${prompt} ${filenames.join(" ")}`.toLowerCase();

  if (
    source.includes("red chamber") ||
    source.includes("honglou") ||
    source.includes("dream of the red chamber") ||
    source.includes("novel")
  ) {
    return "literary";
  }

  if (
    source.includes("public opinion") ||
    source.includes("wuhan") ||
    source.includes("controversy") ||
    source.includes("policy")
  ) {
    return "public-opinion";
  }

  if (
    source.includes("market") ||
    source.includes("finance") ||
    source.includes("stock") ||
    source.includes("earnings")
  ) {
    return "finance";
  }

  return "general";
};

const paletteByScenario: Record<MiroFishScenario, string[]> = {
  "public-opinion": ["#f59e0b", "#f97316", "#ef4444", "#38bdf8", "#a855f7"],
  literary: ["#22c55e", "#38bdf8", "#a855f7", "#f97316", "#eab308"],
  finance: ["#38bdf8", "#22c55e", "#f97316", "#eab308", "#fb7185"],
  general: ["#38bdf8", "#a855f7", "#f97316", "#22c55e", "#eab308"],
};

const buildGraph = (scenario: MiroFishScenario, prompt: string): MiroFishGraphPayload => {
  const colors = paletteByScenario[scenario];
  const nodeNames =
    scenario === "literary"
      ? [
          ["node-1", "Jia Baoyu", "Protagonist"],
          ["node-2", "Lin Daiyu", "Emotional Pivot"],
          ["node-3", "Xue Baochai", "Stability Force"],
          ["node-4", "Grand View Garden", "Setting"],
          ["node-5", "Rumor Network", "Collective Memory"],
        ]
      : scenario === "public-opinion"
        ? [
            ["node-1", "University Administration", "Institution"],
            ["node-2", "Students", "Collective Actor"],
            ["node-3", "Mainstream Media", "Amplifier"],
            ["node-4", "Influencers", "Narrative Driver"],
            ["node-5", "Public Comment Stream", "Sentiment Surface"],
          ]
        : scenario === "finance"
          ? [
              ["node-1", "Management Team", "Institution"],
              ["node-2", "Retail Investors", "Collective Actor"],
              ["node-3", "Analyst Desk", "Interpreter"],
              ["node-4", "Policy Signals", "Macro Force"],
              ["node-5", "Market Narrative", "Sentiment Surface"],
            ]
          : [
              ["node-1", "Seed Event", "Trigger"],
              ["node-2", "Primary Actors", "Collective Actor"],
              ["node-3", "Narrative Layer", "Interpreter"],
              ["node-4", "Social Surface", "Environment"],
              ["node-5", "Constraint Layer", "Pressure"],
            ];

  const nodes = nodeNames.map(([id, name, entityType], index) => ({
    id,
    name,
    entityType,
    summary: `${name} is a high-leverage node in the simulation graph for this scenario.`,
    color: colors[index % colors.length],
    attributes: {
      confidence: `${88 - index * 4}%`,
      influence: `${92 - index * 7}%`,
    },
    labels: [entityType, "Scenario Core"],
  }));

  const edges = [
    {
      id: "edge-1",
      source: "node-1",
      target: "node-2",
      name: "Triggers reaction",
      factType: "trigger",
      fact: "The lead institution or event immediately changes the behavior of core participants.",
      episodes: ["Episode-1", "Episode-2"],
    },
    {
      id: "edge-2",
      source: "node-2",
      target: "node-3",
      name: "Reframes narrative",
      factType: "narrative_shift",
      fact: "Core actors reshape the interpretation layer and move the discourse.",
      episodes: ["Episode-2", "Episode-3"],
    },
    {
      id: "edge-3",
      source: "node-3",
      target: "node-5",
      name: "Amplifies sentiment",
      factType: "amplification",
      fact: "Interpretation pressure spills into the broader public surface.",
      episodes: ["Episode-3", "Episode-4"],
    },
    {
      id: "edge-4",
      source: "node-4",
      target: "node-1",
      name: "Constrains options",
      factType: "constraint",
      fact: "Environmental or setting conditions place limits on strategic response.",
      episodes: ["Episode-1"],
    },
    {
      id: "edge-5",
      source: "node-5",
      target: "node-2",
      name: "Feeds back",
      factType: "feedback",
      fact: "Collective sentiment reshapes the next round of participant behavior.",
      episodes: ["Episode-4", "Episode-5"],
    },
  ];

  return {
    graphId: `graph_${scenario.replace(/[^a-z]/g, "")}_${Math.random().toString(36).slice(2, 8)}`,
    nodeCount: nodes.length,
    edgeCount: edges.length,
    nodes,
    edges,
  };
};

const buildSimulation = (
  scenario: MiroFishScenario,
  prompt: string
): MiroFishSimulationPayload => {
  const focusByScenario: Record<MiroFishScenario, string> = {
    "public-opinion":
      "Forecast how narratives split across institutional messaging, student reactions, and amplification channels.",
    literary:
      "Forecast how hidden motives, emotional pressure, and rumor cascades reshape the lost continuation.",
    finance:
      "Forecast how investor interpretation, macro signals, and public narrative shift price expectations.",
    general:
      "Forecast how the initial seed evolves once collective behavior and memory loops begin to compound.",
  };

  return {
    simulationId: `sim_${scenario.replace(/[^a-z]/g, "")}_${Math.random().toString(36).slice(2, 8)}`,
    projectId: "",
    graphId: "",
    reportId: "",
    status: "idle",
    currentRound: 0,
    totalRounds: 6,
    progressPercent: 0,
    config: {
      maxRounds: 6,
      minutesPerRound: 30,
      graphMemoryUpdates: true,
      enabledPlatforms: ["twitter", "reddit"],
      focus: focusByScenario[scenario],
    },
    profiles: [
      {
        id: "profile-1",
        name: scenario === "literary" ? "Memory Archivist" : "Signal Interpreter",
        role: "Narrative analyst",
        platform: "twitter",
        stance: "High sensitivity to narrative inflection points.",
        motivation: "Surface the fastest-moving interpretation before consensus forms.",
      },
      {
        id: "profile-2",
        name: scenario === "public-opinion" ? "Campus Witness" : "Pressure Observer",
        role: "Primary actor",
        platform: "reddit",
        stance: "Responds to emotional pressure and social legitimacy signals.",
        motivation: "Protect group identity while absorbing new information.",
      },
      {
        id: "profile-3",
        name: scenario === "finance" ? "Macro Narrator" : "Bridge Actor",
        role: "Amplifier",
        platform: "twitter",
        stance: "Converts scattered developments into a legible storyline.",
        motivation: "Turn fragmented signals into a dominant narrative arc.",
      },
    ],
    actions: [],
    posts: [],
  };
};

const buildReport = (
  scenario: MiroFishScenario,
  prompt: string
): MiroFishReportPayload => {
  const titles: Record<MiroFishScenario, string> = {
    "public-opinion": "Public Opinion Trajectory Report",
    literary: "Narrative Continuation Forecast",
    finance: "Market Narrative Forecast",
    general: "Collective Simulation Forecast",
  };

  return {
    reportId: `report_${scenario.replace(/[^a-z]/g, "")}_${Math.random().toString(36).slice(2, 8)}`,
    title: titles[scenario],
    executiveSummary: [
      "The seed event does not remain local. It rapidly converts into a wider interpretation contest.",
      "The first decisive shift happens when institutional framing collides with emotionally legible counter-narratives.",
      "The likely end-state is not a single clean outcome, but a dominant narrative corridor with residual volatility.",
    ],
    predictions: [
      "Round 1-2 establish the moral frame of the scenario.",
      "Round 3-4 redistribute influence toward actors who can compress complexity into a simple story.",
      "Round 5-6 consolidate the winning interpretation and narrow the feasible response set.",
    ],
    risks: [
      "A single new fact could re-open the narrative battlefield after apparent stabilization.",
      "Secondary actors may overreact once social proof suggests a consensus has formed.",
      "The system remains path-dependent: early messaging mistakes compound quickly.",
    ],
    evidence: [
      "Entity graph pressure is concentrated around institutional response, collective memory, and narrative amplifiers.",
      "Simulated actions repeatedly reinforce high-attention surfaces before more technical evidence can circulate.",
      "Conversation velocity remains highest on platforms optimized for emotional compression and repetition.",
    ],
    keyActors: [
      "Institutional frame setters",
      "Narrative amplifiers",
      "Emotionally credible witnesses",
      "Late-stage bridge interpreters",
    ],
    fullReport: [
      `Prompt under analysis: ${prompt}`,
      "The simulation suggests that the seed material contains a strong asymmetry between raw facts and public interpretation. Once the graph is activated, the decisive variable is not only who is correct, but who becomes legible first.",
      "Across the simulated rounds, actors who compress the scenario into a memorable frame gain disproportionate leverage. This does not eliminate nuance; it delays it until the winning narrative corridor has already formed.",
      "The most probable future is a layered stabilization: the public surface converges first, while deeper disagreement continues under the surface. That is why the report should be read as a map of narrative pressure rather than a static verdict.",
    ],
  };
};

export const buildScenarioFixture = (scenario: MiroFishScenario, prompt: string) => {
  const graph = buildGraph(scenario, prompt);
  const simulation = buildSimulation(scenario, prompt);
  const report = buildReport(scenario, prompt);

  return { graph, simulation, report };
};
